import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2, Clock, Sparkles, Mail, CheckCheck,
  Briefcase, Calendar, Users, Radio, LogOut,
} from 'lucide-react';
import { MachhaCharacter } from '../lib/machhaAsset';
import { waitlistApi } from '../api/waitlist';
import { WaitlistEntry } from '../types';
import { useAuthStore } from '../store/authStore';
import { usePageTitle } from '../hooks/usePageTitle';
import { clayStaggerContainer, clayStaggerItem } from '../constants/animations';
import styles from './WaitlistPendingPage.module.css';

/* ── Status stepper ────────────────────────────────────────────────────────── */

const STEPS = [
  { id: 'submitted', label: 'Submitted',    done: true,  active: false },
  { id: 'review',    label: 'Under Review', done: false, active: true  },
  { id: 'decision',  label: 'Decision',     done: false, active: false },
] as const;

/* ── Answer metadata ───────────────────────────────────────────────────────── */

const ANSWER_LABELS: Record<string, { icon: React.ReactNode; label: string }> = {
  role:          { icon: <Briefcase className={styles['icon-xs']} />, label: 'Role'          },
  eventsPerMonth:{ icon: <Calendar  className={styles['icon-xs']} />, label: 'Events / month'},
  teamSize:      { icon: <Users     className={styles['icon-xs']} />, label: 'Team size'     },
  howHeard:      { icon: <Radio     className={styles['icon-xs']} />, label: 'How you heard' },
};

/* ── Page ──────────────────────────────────────────────────────────────────── */

export const WaitlistPendingPage: React.FC = () => {
  usePageTitle('Application Pending');
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const [entry, setEntry] = useState<WaitlistEntry | null>(null);

  useEffect(() => {
    waitlistApi.getMe()
      .then(setEntry)
      .catch(() => navigate('/waitlist/onboarding', { replace: true }));
  }, [navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  return (
    <div className={styles['page']}>
      <motion.div
        className={styles['page-wrapper']}
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
      >

        {/* ── Mascot + heading ─────────────────────────────────────────────── */}
        <div className={styles['hero-section']}>
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ repeat: Infinity, duration: 3.5, ease: 'easeInOut' }}
          >
            <MachhaCharacter state="Milestone" part="body" size={120} />
          </motion.div>

          <h1 className={styles['hero-title']}>
            You're on the list!
          </h1>
          <p className={styles['hero-subtitle']}>
            We're reviewing your application. Most approvals happen within{' '}
            <span className={styles['highlight']}>
              24–48 hours
            </span>.
          </p>
        </div>

        {/* ── Status stepper ───────────────────────────────────────────────── */}
        <div className={styles['card']}>
          <p className={styles['stepper-label']}>
            Application Status
          </p>

          <div className={styles['stepper-row']}>
            {STEPS.map((step, i) => (
              <React.Fragment key={step.id}>
                {/* Step node */}
                <div className={styles['step-node']}>
                  {step.done ? (
                    <div className={styles['step-circle-done']}>
                      <CheckCircle2 className={styles['icon-check']} />
                    </div>
                  ) : step.active ? (
                    <div className={styles['step-circle-active-wrapper']}>
                      <motion.div
                        className={styles['step-pulse']}
                        animate={{ scale: [1, 1.6, 1], opacity: [0.5, 0, 0.5] }}
                        transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
                      />
                      <div className={styles['step-circle-active']}>
                        <Clock className={styles['icon-clock']} />
                      </div>
                    </div>
                  ) : (
                    <div className={styles['step-circle-pending']}>
                      <Sparkles className={styles['icon-sparkle']} />
                    </div>
                  )}
                  <span className={
                    step.done   ? styles['step-label-done']
                    : step.active ? styles['step-label-active']
                    : styles['step-label-pending']
                  }>
                    {step.label}
                  </span>
                </div>

                {/* Connector */}
                {i < STEPS.length - 1 && (
                  <div className={i === 0 ? styles['connector-done'] : styles['connector-pending']} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* ── What happens next ────────────────────────────────────────────── */}
        <div className={styles['card']}>
          <p className={styles['next-label']}>
            What happens next
          </p>
          <div className={styles['next-list']}>
            {[
              {
                icon: <Clock className={[styles['icon-xs'], styles['icon-clock']].join(' ')} />,
                bgClass: styles['next-icon-amber'],
                title: '24–48 hour review',
                body: 'Our team reviews every application manually to keep quality high.',
              },
              {
                icon: <Mail className={styles['icon-xs']} style={{ color: 'var(--clay-orange)' }} />,
                bgClass: styles['next-icon-orange'],
                title: 'Email notification',
                body: "You'll receive an email the moment your application is approved.",
              },
            ].map(({ icon, bgClass, title, body }) => (
              <div key={title} className={styles['next-item']}>
                <div className={bgClass}>
                  {icon}
                </div>
                <div>
                  <p className={styles['next-title']}>{title}</p>
                  <p className={styles['next-body']}>{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Your Application ─────────────────────────────────────────────── */}
        <AnimatePresence>
          {entry && (
            <motion.div
              className={styles['card']}
              variants={clayStaggerContainer}
              initial="hidden"
              animate="visible"
            >
              <div className={styles['app-header']}>
                <p className={styles['app-label']}>
                  Your Application
                </p>
                <span className={styles['app-status']}>
                  <CheckCheck className={styles['icon-check-sm']} />
                  All details submitted
                </span>
              </div>

              <div className={styles['app-list']}>
                {(Object.keys(ANSWER_LABELS) as Array<keyof typeof ANSWER_LABELS>).map((key) => {
                  const value = entry.answers[key as keyof typeof entry.answers];
                  if (!value) return null;
                  const meta = ANSWER_LABELS[key];
                  return (
                    <motion.div
                      key={key}
                      variants={clayStaggerItem}
                      className={styles['app-row']}
                    >
                      <span className={styles['app-row-icon']}>
                        {meta.icon}
                      </span>
                      <span className={styles['app-row-label']}>
                        {meta.label}
                      </span>
                      <span className={styles['app-row-value']}>
                        {value}
                      </span>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Sign out ─────────────────────────────────────────────────────── */}
        <div className={styles['signout-row']}>
          <button
            onClick={handleLogout}
            className={styles['signout-btn']}
          >
            <LogOut className={styles['icon-signout']} />
            Sign out
          </button>
        </div>

      </motion.div>
    </div>
  );
};
